import React, { useState, useEffect } from 'react'
import { apiClient } from '../lib/apiClient'

interface AnalyticsData {
  userRegistrations: Array<{
    _id: { year: number; month: number; day: number }
    count: number
  }>
  userActivity: Array<{
    _id: { year: number; month: number; day: number }
    count: number
  }>
  preferencesData: Array<{
    _id: string
    count: number
  }>
  locationData: Array<{
    _id: string
    count: number
  }>
}

const AdminAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState('30d')

  useEffect(() => {
    fetchAnalytics()
  }, [period])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(`/admin/analytics/users?period=${period}`)
      setAnalytics(response)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateData: { year: number; month: number; day: number }) => {
    return new Date(dateData.year, dateData.month - 1, dateData.day).toLocaleDateString()
  }

  const getPeriodLabel = () => {
    switch (period) {
      case '7d': return 'Last 7 days'
      case '30d': return 'Last 30 days'
      case '90d': return 'Last 90 days'
      case '1y': return 'Last year'
      default: return 'Last 30 days'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
              <p className="mt-1 text-sm text-gray-500">
                User behavior and engagement insights
              </p>
            </div>
            <div className="flex space-x-4">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={fetchAnalytics}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Registrations Chart */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            User Registrations - {getPeriodLabel()}
          </h3>
          <div className="h-64 overflow-x-auto">
            <div className="flex items-end space-x-2 h-full min-w-max">
              {analytics?.userRegistrations.map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className="bg-blue-500 rounded-t w-8 min-h-2"
                    style={{ height: `${Math.max((item.count / Math.max(...analytics.userRegistrations.map(i => i.count))) * 200, 4)}px` }}
                    title={`${formatDate(item._id)}: ${item.count} users`}
                  ></div>
                  <div className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                    {item._id.month}/{item._id.day}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Total registrations: {analytics?.userRegistrations.reduce((sum, item) => sum + item.count, 0)}
          </div>
        </div>

        {/* User Activity Chart */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            User Activity - {getPeriodLabel()}
          </h3>
          <div className="h-64 overflow-x-auto">
            <div className="flex items-end space-x-2 h-full min-w-max">
              {analytics?.userActivity.map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className="bg-green-500 rounded-t w-8 min-h-2"
                    style={{ height: `${Math.max((item.count / Math.max(...analytics.userActivity.map(i => i.count))) * 200, 4)}px` }}
                    title={`${formatDate(item._id)}: ${item.count} active users`}
                  ></div>
                  <div className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-left">
                    {item._id.month}/{item._id.day}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Total active sessions: {analytics?.userActivity.reduce((sum, item) => sum + item.count, 0)}
          </div>
        </div>

        {/* Preferences and Location Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Popular Cuisines */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Popular Cuisines</h3>
            <div className="space-y-3">
              {analytics?.preferencesData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">{item._id}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(item.count / Math.max(...analytics.preferencesData.map(i => i.count))) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Locations */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">User Locations (ZIP Codes)</h3>
            <div className="space-y-3">
              {analytics?.locationData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{item._id}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{
                          width: `${(item.count / Math.max(...analytics.locationData.map(i => i.count))) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Summary Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {analytics?.userRegistrations.reduce((sum, item) => sum + item.count, 0) || 0}
              </div>
              <div className="text-sm text-gray-500">New Registrations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {analytics?.userActivity.reduce((sum, item) => sum + item.count, 0) || 0}
              </div>
              <div className="text-sm text-gray-500">Active Sessions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {analytics?.preferencesData.length || 0}
              </div>
              <div className="text-sm text-gray-500">Cuisine Preferences</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminAnalytics
